import React, { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { CreateShortcodeRequest } from '../types'
import { createShortcode } from '../api'

interface CreateShortcodeProps {
  onSuccess: (shortcode: any) => void
  onCancel: () => void
}

const CreateShortcode: React.FC<CreateShortcodeProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<CreateShortcodeRequest>({
    url: '',
    description: '',
    shortcode: '',
    androidUrl: '',
    iosUrl: '',
    linuxUrl: '',
    macUrl: '',
    windowsUrl: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const result = await createShortcode(formData)
      if (result.success && result.data) {
        onSuccess({
          ...result.data,
          createdAt: new Date().toISOString()
        })
      } else {
        setError(result.error || 'Failed to create shortcode')
      }
    } catch (err) {
      setError('An error occurred while creating the shortcode')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <div className="card hover-lift scale-in">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h2 className="text-heading text-gray-900">Create New Shortcode</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors focus-ring p-1 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card-body space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 fade-in">
            <p className="text-red-800 text-sm font-medium">{error}</p>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="url" className="form-label">
            Destination URL *
          </label>
          <input
            type="url"
            id="url"
            name="url"
            value={formData.url}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="https://example.com"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="form-label">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={3}
            className="form-input resize-none"
            placeholder="Brief description of this shortcode"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="shortcode" className="form-label">
            Custom Shortcode (Optional)
          </label>
          <input
            type="text"
            id="shortcode"
            name="shortcode"
            value={formData.shortcode}
            onChange={handleChange}
            pattern="[a-zA-Z0-9]{4,}"
            className="form-input"
            placeholder="Leave empty for auto-generation"
          />
          <p className="text-muted">
            Must be at least 4 characters, letters and numbers only
          </p>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-subheading text-gray-900 mb-4">Platform-Specific URLs (Optional)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['android', 'ios', 'linux', 'mac', 'windows'].map((platform) => (
              <div key={platform} className="space-y-2">
                <label htmlFor={`${platform}Url`} className="form-label">
                  {platform.charAt(0).toUpperCase() + platform.slice(1)} URL
                </label>
                <input
                  type="url"
                  id={`${platform}Url`}
                  name={`${platform}Url`}
                  value={formData[`${platform}Url` as keyof CreateShortcodeRequest] as string}
                  onChange={handleChange}
                  className="form-input"
                  placeholder={`https://${platform}.example.com`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="loading-spinner h-4 w-4"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                <span>Create Shortcode</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CreateShortcode